import React, { useContext, useState, useMemo } from "react";
import {
  ITravelOfferCardContent,
  TravelWalletSingleSelectionWorkflow,
  TravelWalletSingleSelectionStep,
  MobilePopoverCard,
  BackButton,
  MobileFloatingButton,
  getTravelOfferCardContent,
  B2BSpinner,
  LoadingIndicator,
  getTravelCreditCardContent,
  getTravelCreditHistoryModalProps,
  IconName,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
} from "halifax";
import {
  CallState,
  SelectedTravelOfferScreen,
  SELECTED_TRAVEL_OFFER,
  VIEWED_OFFER_FAQ,
  TravelWalletOffer,
  TravelWalletCredit,
} from "redmond";
import {
  CONTINUE,
  CREDITS_CTA,
  CREDIT_MODAL_HEADER,
  getWalletModalTitle,
  getSelectedCreditDescription,
  getSelectedOfferDescription,
  LOADING_OFFERS,
  VIEW_OFFER_DETAILS_CTA_COPY,
  LOADING_WALLET,
} from "./textConstants";
import { TravelOfferSelectionConnectorProps } from "./container";
import { RouteComponentProps } from "react-router-dom";
import "./styles.scss";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";
import {
  AVAILABLE,
  CONTROL,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_SALE,
  TRAVEL_SALE_VARIANTS,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  useExperiments,
} from "../../../../context/experiments";
import { ClientContext } from "../../../../App";
import { config } from "../../../../api/config";

const DO_NOT_APPLY_REWARDS_KEY = "do-not-apply-rewards";

export interface ITravelOfferSelectionProps
  extends TravelOfferSelectionConnectorProps,
    RouteComponentProps {
  className?: string;
  isMobile?: boolean;
  progressStep?: TravelWalletSingleSelectionStep;
  mobileHeaderElement?: JSX.Element;
  onGoBack?: () => void;
  onContinueClick?: () => void;
}

export const TravelOfferSelection = (props: ITravelOfferSelectionProps) => {
  const {
    className,
    offers,
    bestOverallOffer,
    offerToApply,
    setOfferToApply,
    isMobile,
    progressStep,
    mobileHeaderElement,
    onGoBack,
    onContinueClick,
    fetchTravelWalletOffersCallState,
    selectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    setSelectedRewardsPaymentTotal,
    resetPaymentCardSelectedAccounts,
    credit,
    creditBreakdown,
    creditToApply,
    setCreditToApply,
    maxApplicableTravelWalletCreditOnly,
    isTravelWalletPaymentOnly,
    creditsTransactionHistory,
  } = props;
  const [travelOfferCardContent, setTravelOfferCardContent] = useState<
    ITravelOfferCardContent | undefined
  >(undefined);
  const [currentSelectedOffer, setCurrentSelectedOffer] = useState<
    TravelWalletOffer | undefined
  >(undefined);
  const [currentSelectedCredit, setCurrentSelectedCredit] = useState<
    TravelWalletCredit | undefined
  >(undefined);
  const [openModal, setOpenModal] = useState(false);

  const expState = useExperiments();
  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(() => {
    return travelCreditHistoryExperiment === AVAILABLE;
  }, [travelCreditHistoryExperiment]);

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS
  );
  const isTravelSaleEnabled = travelSaleVariant !== CONTROL;

  const firstName =
    useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const isLoading = fetchTravelWalletOffersCallState === CallState.InProcess;

  const filteredOffers = useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers]
  );

  const filteredBestOverallOffer = useMemo(
    () =>
      bestOverallOffer && isSpendAndGetOffer(bestOverallOffer.descriptions)
        ? undefined
        : bestOverallOffer,
    [bestOverallOffer]
  );

  const trackViewedOffer = () => {
    trackEvent({
      eventName: VIEWED_OFFER_FAQ,
      properties: {
        screen: SelectedTravelOfferScreen.CARS_CHECKOUT,
      },
    });
  };

  React.useEffect(() => {
    switch (progressStep) {
      case TravelWalletSingleSelectionStep.Main:
        setOpenModal(false);
        break;
      case TravelWalletSingleSelectionStep.TravelWalletSelection:
        setOpenModal(true);
        break;
      default:
        break;
    }
  }, [progressStep]);

  React.useEffect(() => {
    if (
      (!isTravelWalletCreditsExperiment && filteredBestOverallOffer) ||
      Math.abs(filteredBestOverallOffer?.amount.amount || 0) >
        Math.abs(credit?.amount.amount || 0)
    ) {
      setOfferToApply(filteredBestOverallOffer);
      setCurrentSelectedOffer(filteredBestOverallOffer);
      setCreditToApply(undefined);
      setCurrentSelectedCredit(undefined);
    } else if (credit) {
      setCreditToApply(credit);
      setCurrentSelectedCredit(credit);
      setOfferToApply(undefined);
      setCurrentSelectedOffer(undefined);
    }
  }, [filteredBestOverallOffer, credit]);

  React.useEffect(() => {
    if (currentSelectedOffer) {
      const content = getTravelOfferCardContent(currentSelectedOffer);
      setTravelOfferCardContent(content);
    }
  }, [currentSelectedOffer]);

  React.useEffect(() => {
    const hasRealRewardsSelection =
      selectedRewardsPaymentAccountId &&
      selectedRewardsPaymentAccountId !== DO_NOT_APPLY_REWARDS_KEY;

    if (selectedPaymentMethodId || hasRealRewardsSelection) {
      resetPaymentCardSelectedAccounts();
      setSelectedRewardsPaymentTotal("", null, null, null);
    }
  }, [offerToApply, creditToApply]);

  const creditDetailsCardProps = React.useMemo(
    () =>
      getTravelCreditCardContent(
        creditBreakdown,
        config.TENANT,
        firstName,
        credit?.amount
      ),
    [firstName, credit, creditBreakdown]
  );

  if ((!filteredOffers || !filteredBestOverallOffer) && !credit && !isLoading) return null;

  return isMobile ? (
    <MobilePopoverCard
      className={clsx("mobile-select-offers-popup", "b2b")}
      open={openModal}
      onClose={() => setOpenModal(false)}
      fullScreen
      headerElement={mobileHeaderElement}
      topLeftButton={
        onGoBack && (
          <BackButton
            className="car-offers-popup-back-button"
            onClick={onGoBack}
          />
        )
      }
    >
      <TravelWalletSingleSelectionWorkflow
        travelOfferCardContent={travelOfferCardContent}
        modalCTACopy={
          currentSelectedCredit ? CREDITS_CTA : VIEW_OFFER_DETAILS_CTA_COPY
        }
        modalHeaderText={
          currentSelectedCredit ? CREDIT_MODAL_HEADER(firstName) : undefined
        }
        offers={filteredOffers || []}
        className={className}
        offerToApply={offerToApply}
        setOfferToApply={setOfferToApply}
        currentSelectedOffer={currentSelectedOffer}
        setCurrentSelectedOffer={setCurrentSelectedOffer}
        getOfferDescription={getSelectedOfferDescription}
        modalTitle={getWalletModalTitle(filteredOffers || [], credit, true)}
        isMobile={isMobile}
        onSetOpenModal={(open) => {
          if (open && offerToApply) {
            trackEvent({
              eventName: SELECTED_TRAVEL_OFFER,
              properties: {
                screen: SelectedTravelOfferScreen.CARS_CHECKOUT,
                offer_count: filteredOffers?.length,
                ...offerToApply?.trackingPropertiesV2?.properties,
              },
              encryptedProperties: [
                offerToApply?.trackingPropertiesV2?.encryptedProperties ?? "",
              ],
            });
          }
        }}
        onFirstTravelOfferCardChange={trackViewedOffer}
        credit={isTravelWalletCreditsExperiment ? credit : undefined}
        creditToApply={creditToApply}
        setCreditToApply={setCreditToApply}
        currentSelectedCredit={currentSelectedCredit}
        setCurrentSelectedCredit={setCurrentSelectedCredit}
        getCreditDescription={getSelectedCreditDescription}
        creditAmountToApply={maxApplicableTravelWalletCreditOnly}
        creditDetailsCardProps={{ cardContent: creditDetailsCardProps }}
        isTravelWalletPaymentOnly={isTravelWalletPaymentOnly}
        showCreditHistory={
          isTravelCreditHistoryExperiment && !!creditsTransactionHistory?.length
        }
        travelCreditHistoryProps={
          isTravelCreditHistoryExperiment && !!creditsTransactionHistory?.length
            ? getTravelCreditHistoryModalProps(
                firstName,
                creditsTransactionHistory,
                credit
              )
            : undefined
        }
        isTravelSale={isTravelSaleEnabled}
      />
      {onContinueClick && (
        <MobileFloatingButton
          className="offers-continue-button"
          onClick={onContinueClick}
          wrapperClassName="b2b"
        >
          {CONTINUE}
        </MobileFloatingButton>
      )}
    </MobilePopoverCard>
  ) : (
    <Box className="checkout-travel-offer-container">
      {isLoading ? (
        <LoadingIndicator
          indicator={B2BSpinner}
          message={
            isTravelWalletCreditsExperiment ? LOADING_WALLET : LOADING_OFFERS
          }
          indicatorSize="medium"
          textAlign="center"
        />
      ) : (
        <TravelWalletSingleSelectionWorkflow
          travelOfferCardContent={travelOfferCardContent}
          modalCTACopy={
            currentSelectedCredit ? CREDITS_CTA : VIEW_OFFER_DETAILS_CTA_COPY
          }
          modalHeaderText={
            currentSelectedCredit ? CREDIT_MODAL_HEADER(firstName) : undefined
          }
          offers={filteredOffers || []}
          className={className}
          offerToApply={offerToApply}
          setOfferToApply={setOfferToApply}
          currentSelectedOffer={currentSelectedOffer}
          setCurrentSelectedOffer={setCurrentSelectedOffer}
          getOfferDescription={getSelectedOfferDescription}
          modalTitle={getWalletModalTitle(filteredOffers || [], credit, false)}
          isMobile={isMobile}
          onSetOpenModal={(open) => {
            if (open) {
              trackEvent({
                eventName: SELECTED_TRAVEL_OFFER,
                properties: {
                  screen: SelectedTravelOfferScreen.CARS_CHECKOUT,
                  offer_count: filteredOffers?.length,
                  ...offerToApply?.trackingPropertiesV2?.properties,
                },
                encryptedProperties: [
                  offerToApply?.trackingPropertiesV2?.encryptedProperties ?? "",
                ],
              });
            }
          }}
          onFirstTravelOfferCardChange={trackViewedOffer}
          credit={isTravelWalletCreditsExperiment ? credit : undefined}
          creditToApply={creditToApply}
          setCreditToApply={setCreditToApply}
          currentSelectedCredit={currentSelectedCredit}
          setCurrentSelectedCredit={setCurrentSelectedCredit}
          getCreditDescription={getSelectedCreditDescription}
          creditAmountToApply={maxApplicableTravelWalletCreditOnly}
          creditDetailsCardProps={{ cardContent: creditDetailsCardProps }}
          isTravelWalletPaymentOnly={isTravelWalletPaymentOnly}
          showCreditHistory={
            isTravelCreditHistoryExperiment &&
            !!creditsTransactionHistory?.length
          }
          travelCreditHistoryProps={
            isTravelCreditHistoryExperiment &&
            !!creditsTransactionHistory?.length
              ? getTravelCreditHistoryModalProps(
                  firstName,
                  creditsTransactionHistory,
                  credit,
                  IconName.PiggyBankCircleOutline
                )
              : undefined
          }
          isTravelSale={isTravelSaleEnabled}
        />
      )}
    </Box>
  );
};
